import { useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import Group from 'components/utils/Group'
import FilterChip from 'LuxKit/Chips/FilterChip'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import LineExternalLinkAltIcon from 'LuxKit/Icons/line/LineExternalLinkAltIcon'

export type ListingDrawerHeaderActionsVertical = 'hotel' | 'experience'

export interface ListingDrawerHeaderActionsProps {
  showBrowseAllAction?: boolean
  browseAllResultsUrl?: string
  onExploreMap?: () => void
  isMobileScreen: boolean
  vertical: ListingDrawerHeaderActionsVertical
}

function getBrowseAllResultsLabel(
  intl: IntlShape,
  vertical: ListingDrawerHeaderActionsVertical,
): string {
  switch (vertical) {
    case 'hotel':
      return intl.formatMessage({
        id: 'itl8WEctxs1Z0nf23fD3',
        defaultMessage: 'Browse all results',
        description:
          'Button label linking to the full hotel search results page',
      })
    case 'experience':
      return intl.formatMessage({
        id: 'nemeMlkPWyHbg8VU1XwY',
        defaultMessage: 'Browse all results',
        description:
          'Button label linking to the full experience search results page',
      })
    default: {
      const exhaustiveCheck: never = vertical
      return exhaustiveCheck
    }
  }
}

function getMapExploreLabel(
  intl: IntlShape,
  vertical: ListingDrawerHeaderActionsVertical,
  isMobileScreen: boolean,
): string {
  if (isMobileScreen) {
    switch (vertical) {
      case 'hotel':
        return intl.formatMessage({
          id: '9riZb6FinKgw_bgJCPsX',
          defaultMessage: 'Map view',
          description:
            'Mobile chip label on the trip planner hotel listing drawer header that opens map explore for hotels at the current itinerary stop.',
        })
      case 'experience':
        return intl.formatMessage({
          id: 'M49sbsbxqURDCsnnoyU7',
          defaultMessage: 'Map view',
          description:
            'Mobile chip label on the trip planner experience listing drawer header that opens map explore for experiences at the current itinerary stop.',
        })
      default: {
        const exhaustiveCheck: never = vertical
        return exhaustiveCheck
      }
    }
  }

  switch (vertical) {
    case 'hotel':
      return intl.formatMessage({
        id: 'jcH-AcKGdZjLXZnnZGK9',
        defaultMessage: 'Explore more',
        description:
          'Chip label on the trip planner hotel listing drawer header that opens map explore for hotels at the current itinerary stop.',
      })
    case 'experience':
      return intl.formatMessage({
        id: 'cAg7XWiAJmj99PC48hv3',
        defaultMessage: 'Explore more',
        description:
          'Chip label on the trip planner experience listing drawer header that opens map explore for experiences at the current itinerary stop.',
      })
    default: {
      const exhaustiveCheck: never = vertical
      return exhaustiveCheck
    }
  }
}

function ListingDrawerHeaderActions({
  showBrowseAllAction = false,
  browseAllResultsUrl,
  onExploreMap,
  isMobileScreen,
  vertical,
}: ListingDrawerHeaderActionsProps) {
  const intl = useIntl()

  if (!showBrowseAllAction && !onExploreMap) {
    return null
  }

  const browseAllResultsLabel = getBrowseAllResultsLabel(intl, vertical)
  const mapExploreLabel = getMapExploreLabel(intl, vertical, isMobileScreen)

  return (
    <Group direction="horizontal" gap={8} verticalAlign="center" horizontalAlign="end" wrap="wrap">
      {showBrowseAllAction && (
        <FilterChip
          size="small"
          href={browseAllResultsUrl}
          target="_blank"
          rel="noopener noreferrer"
          endIcon={<LineExternalLinkAltIcon />}
        >
          {browseAllResultsLabel}
        </FilterChip>
      )}
      {onExploreMap && (
        <FilterChip
          size="small"
          startIcon={<LineMapIcon />}
          onClick={onExploreMap}
        >
          {mapExploreLabel}
        </FilterChip>
      )}
    </Group>
  )
}

export default ListingDrawerHeaderActions
